import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/layout';
import { Card, CardBody } from '../components/common';
import { useAuth } from '../hooks/useAuth';

export const OAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithToken, isAuthenticated } = useAuth();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      // Check for error in query params (from backend redirect)
      const errorParam = searchParams.get('error');
      const messageParam = searchParams.get('message');

      if (errorParam) {
        let errorMessage = 'Authentication failed';

        switch (errorParam) {
          case 'email_exists':
            errorMessage = messageParam || 'An account with this email already exists. Please log in with your password.';
            break;
          case 'invalid_state':
            errorMessage = 'Authentication session expired. Please try again.';
            break;
          case 'missing_params':
            errorMessage = 'Invalid authentication response. Please try again.';
            break;
          case 'access_denied':
            errorMessage = 'You denied access to your account.';
            break;
          default:
            errorMessage = messageParam || 'Authentication failed. Please try again.';
        }

        setError(errorMessage);
        setProcessing(false);
        return;
      }

      // Extract token from URL fragment
      const hash = window.location.hash;
      if (!hash) {
        setError('No authentication token received');
        setProcessing(false);
        return;
      }

      const params = new URLSearchParams(hash.substring(1));
      const token = params.get('token');

      if (!token) {
        setError('Invalid authentication response');
        setProcessing(false);
        return;
      }

      try {
        await loginWithToken(token);
        // Clear the hash from the URL for security
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/dashboard', { replace: true });
      } catch (err) {
        setError(err.message || 'Failed to complete authentication');
        setProcessing(false);
      }
    };

    if (!isAuthenticated) {
      handleCallback();
    } else {
      navigate('/dashboard', { replace: true });
    }
  }, [searchParams, loginWithToken, navigate, isAuthenticated]);

  if (error) {
    return (
      <Layout>
        <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4">
          <Card className="w-full max-w-md">
            <CardBody>
              <div className="text-center">
                <div className="text-red-500 text-4xl mb-4">!</div>
                <h2 className="text-xl font-bold mb-4">Authentication Error</h2>
                <p className="text-gray-600 mb-6">{error}</p>
                <div className="space-y-2">
                  <button
                    onClick={() => navigate('/login')}
                    className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Back to Login
                  </button>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md">
          <CardBody>
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <h2 className="text-xl font-bold mb-2">Completing Sign In</h2>
              <p className="text-gray-600">Please wait...</p>
            </div>
          </CardBody>
        </Card>
      </div>
    </Layout>
  );
};
